//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
import { type Class, type Ref } from '@hcengineering/core'
import { type Asset, type IntlString, type Metadata, plugin, type Plugin } from '@hcengineering/platform'
import { AnyComponent } from '@hcengineering/ui'
import { Tier } from './types'

/** @public */
export const billingId = 'billing' as Plugin

export const billingPlugin = plugin(billingId, {
  class: {
    Tier: '' as Ref<Class<Tier>>
  },
  metadata: {
    BillingURL: '' as Metadata<string>
  },
  component: {
    Settings: '' as AnyComponent,
    WorkbenchExtension: '' as AnyComponent,
    UsageExtension: '' as AnyComponent,
    LimitsFooterIndicator: '' as AnyComponent
  },
  string: {
    Billing: '' as IntlString,
    DriveSize: '' as IntlString,
    DriveCount: '' as IntlString,
    OfficeSessionsDuration: '' as IntlString,
    OfficeSessionsBandwidth: '' as IntlString,
    OfficeEgressDuration: '' as IntlString,
    AI: '' as IntlString,
    TotalTokens: '' as IntlString,
    TranscriptionTime: '' as IntlString,
    Tier: '' as IntlString,
    StorageLimit: '' as IntlString,
    TrafficLimit: '' as IntlString,
    Common: '' as IntlString,
    CommonDescription: '' as IntlString,
    Rare: '' as IntlString,
    RareDescription: '' as IntlString,
    Epic: '' as IntlString,
    EpicDescription: '' as IntlString,
    Legendary: '' as IntlString,
    LegendaryDescription: '' as IntlString,
    UpgradePlan: '' as IntlString,
    LimitReached: '' as IntlString,
    LimitsExceededShort: '' as IntlString,
    LimitExceededWarningTitle: '' as IntlString,
    LimitExceededWarningHint: '' as IntlString,
    LimitExceededRestrictedTitle: '' as IntlString,
    LimitExceededRestrictedHint: '' as IntlString,
    UploadRestrictedToast: '' as IntlString,
    UpgradePlanCta: '' as IntlString,
    TabModels: '' as IntlString,
    TabCalculator: '' as IntlString,
    TabUsage: '' as IntlString,
    ModelLimits: '' as IntlString,
    ResetAllUsed: '' as IntlString,
    ResetAllUsedConfirm: '' as IntlString,
    AddTokens: '' as IntlString,
    ResetUsed: '' as IntlString,
    NoLimit: '' as IntlString,
    PurchasedTokens: '' as IntlString,
    SetLimit: '' as IntlString,
    SetUsed: '' as IntlString,
    PickModel: '' as IntlString,
    SectionLLM: '' as IntlString,
    SectionASR: '' as IntlString,
    AsrByModel: '' as IntlString,
    NoData: '' as IntlString,
    Workers: '' as IntlString,
    ByLevel: '' as IntlString,
    Total: '' as IntlString,
    DirectProvider: '' as IntlString,
    Calculator: '' as IntlString,
    CurrencyUsd: '' as IntlString,
    CurrencyBrl: '' as IntlString,
    ExchangeRateLabel: '' as IntlString,
    ExchangeRateAt: '' as IntlString,
    ExchangeRateSource: '' as IntlString,
    ExchangeRateError: '' as IntlString,
    ExchangeRateManual: '' as IntlString,
    CostTableTitle: '' as IntlString,
    CostTableProvider: '' as IntlString,
    CostTableModel: '' as IntlString,
    CostTableUnit: '' as IntlString,
    CostTableInput: '' as IntlString,
    CostTableOutput: '' as IntlString,
    CostUnitPer1M: '' as IntlString,
    CostUnitPerMinute: '' as IntlString,
    CostOverrideBadge: '' as IntlString,
    RestoreDefaults: '' as IntlString,
    HistoryTitle: '' as IntlString,
    HistoryTokens30d: '' as IntlString,
    HistoryMinutes30d: '' as IntlString,
    HistoryActiveSeats: '' as IntlString,
    HistoryAvgTokensPerUser: '' as IntlString,
    HistoryAvgMinutesPerUser: '' as IntlString,
    HistoryEmpty: '' as IntlString,
    ApplyHistory: '' as IntlString,
    SimTitle: '' as IntlString,
    SimPlan: '' as IntlString,
    SimSeats: '' as IntlString,
    SimTokensPerUser: '' as IntlString,
    SimOutputShare: '' as IntlString,
    SimMinutesPerUser: '' as IntlString,
    SimInfraCost: '' as IntlString,
    SimRevenue: '' as IntlString,
    SimAiCostByModel: '' as IntlString,
    SimAiCostTotal: '' as IntlString,
    SimTotalCost: '' as IntlString,
    SimMargin: '' as IntlString,
    SimMarginPct: '' as IntlString,
    PlanConfigError: '' as IntlString,
    TokenWindowMonth: '' as IntlString,
    ResetsAt: '' as IntlString,
    TokenAvailable: '' as IntlString,
    TokenPurchased: '' as IntlString
  },
  icon: {
    Billing: '' as Asset,
    Subscriptions: '' as Asset
  },
  tier: {
    Common: '' as Ref<Tier>,
    Rare: '' as Ref<Tier>,
    Epic: '' as Ref<Tier>,
    Legendary: '' as Ref<Tier>
  }
})

export default billingPlugin
